import React, { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Sparkles, Loader2, Anchor } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import { legacyPromote } from "../lib/api";

export const LegacyPromotion = ({ cadet, mentors, onPromoted }) => {
  const [loading, setLoading] = useState(false);
  const isNavigator = cadet?.rank === "Navigator";
  const myPrivateer = (mentors || []).find(m => m.is_legacy && m.legacy_cadet_id === cadet?.id);
  const promote = async () => {
    setLoading(true);
    try {
      const r = await legacyPromote();
      onPromoted && onPromoted(r.mentor);
    } finally { setLoading(false); }
  };

  if (!isNavigator) {
    return (
      <div className="eigoo-glass rounded-3xl p-6 border-yellow-300/15 opacity-70" data-testid="legacy-locked">
        <div className="flex items-center gap-3 mb-3">
          <Crown className="text-white/40" size={22} />
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-display">Legacy Cohort</div>
            <div className="font-display font-bold text-lg text-white/70">Reach Navigator rank to charter your Privateer</div>
          </div>
        </div>
        <p className="text-sm text-white/50">
          When you ascend to Navigator, you may charter a Privateer in your name and pass the lessons forward.
          Current rank: <span className="text-yellow-300 font-bold">{cadet?.rank || "Cadet"}</span>.
        </p>
      </div>
    );
  }

  if (myPrivateer) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="eigoo-glass rounded-3xl p-6 eigoo-glow-gold"
        data-testid="legacy-active"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="relative h-14 w-14 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center">
            <Crown className="text-black" size={26} />
            <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-red-500 border-2 border-black flex items-center justify-center">
              <Anchor className="text-white" size={12} />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-yellow-300 font-display">Legacy Cohort · Active</div>
            <div className="font-display font-black text-2xl">{myPrivateer.name}</div>
            <div className="text-xs text-yellow-300/70">{myPrivateer.title}</div>
          </div>
          <SpeakerButton
            text={`Your Privateer ${myPrivateer.name} is now in the roster. ${myPrivateer.bio}`}
            testid="speaker-legacy-active"
            size="sm"
          />
        </div>
        <p className="text-white/80 text-sm leading-relaxed mb-3">"{myPrivateer.bio}"</p>
        <div className="text-xs text-white/50">
          Pick your Privateer in the roster to author signals as them — for journaling, mentor practice, or building your legacy thread.
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="eigoo-glass rounded-3xl p-6 eigoo-glow-gold relative overflow-hidden"
      data-testid="legacy-promote"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 80% 20%, rgba(255,215,0,0.25), transparent 60%)" }} />
      <div className="relative">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="text-yellow-300" size={20} />
          <div className="text-xs uppercase tracking-[0.3em] text-yellow-300 font-display font-bold">Navigator Privilege Unlocked</div>
        </div>
        <h3 className="font-display font-black text-3xl md:text-4xl leading-tight mb-2">
          Charter your <span className="text-yellow-300">Privateer</span>.
        </h3>
        <p className="text-white/75 text-sm md:text-base leading-relaxed mb-5 max-w-xl">
          You reached Navigator. The Fleet now permits you to charter a Privateer in your callsign — a permanent Mentor identity on the roster. Pass the lessons forward.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={promote}
            disabled={loading}
            className="btn-eigoo btn-gold"
            data-testid="charter-privateer-btn"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Chartering...</> : <><Crown size={18} /> Charter Privateer {cadet.callsign}</>}
          </button>
          <SpeakerButton
            text={`You can now charter a Privateer in your name. Tap charter to add ${cadet.callsign || "your Privateer"} to the roster.`}
            testid="speaker-promote"
            size="sm"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LegacyPromotion;
